import { useState } from 'react'
import { Alert, Box, Button, Divider, Stack, TextField, Typography } from '@mui/material'

import BaseButton from '@/components/base/button'
import { useAppStore } from '@/contexts/store'

import { useAuth } from '@/api/auth/use-auth'

type InlineAuthFormProps = {
  /** Optional message shown above the form */
  message?: string | null
}

/**
 * Inline login form + registration link, designed to be embedded
 * inside dialogs that require authentication before an action.
 * Once the user logs in successfully, the parent re-renders with
 * isAuthenticated === true and can show the original form.
 */
const InlineAuthForm = ({ message }: InlineAuthFormProps) => {
  const { login } = useAuth()
  const { strings } = useAppStore()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleLogin = async () => {
    setError(null)
    setIsLoading(true)

    try {
      await login({ email, password })
    } catch {
      setError(
        strings?.advertising?.inline_auth?.login_failed ||
          'Login fallito. Controlla le tue credenziali e riprova.'
      )
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Stack spacing={3}>
      {message ? (
        <Typography variant="textRegular" color="text.secondary" sx={{ '& p': { margin: 0 } }}>
          {message}
        </Typography>
      ) : null}

      <Typography variant="h6" fontWeight={600}>
        {strings?.advertising?.inline_auth?.login_title || 'Accedi per continuare'}
      </Typography>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <TextField
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        fullWidth
        size="small"
        autoComplete="email"
      />

      <TextField
        label="Password"
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        fullWidth
        size="small"
        autoComplete="current-password"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            handleLogin()
          }
        }}
      />

      <Button
        variant="contained"
        onClick={handleLogin}
        loading={isLoading}
        disabled={isLoading || !email || !password}
        fullWidth
      >
        {strings?.advertising?.inline_auth?.login_button || 'Accedi'}
      </Button>

      <Divider>
        <Typography variant="caption" color="text.secondary">
          {strings?.advertising?.inline_auth?.or_label || 'oppure'}
        </Typography>
      </Divider>

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="textRegular" color="text.secondary" sx={{ mb: 1 }}>
          {strings?.advertising?.inline_auth?.register_prompt || 'Non hai un account?'}
        </Typography>
        {strings?.advertising?.link_login?.url ? (
          <Box sx={{ mt: 1 }}>
            <BaseButton icon="user" href={strings.advertising.link_login.url}>
              {strings?.advertising?.inline_auth?.register_button ||
                strings?.advertising?.link_login?.title ||
                'Registrati'}
            </BaseButton>
          </Box>
        ) : null}
      </Box>
    </Stack>
  )
}

export default InlineAuthForm
